import {
  Box,
  Text,
  Stack,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { StripeBillingAccountListingResponse } from '@zap-onboard/api-client';
import { inputs } from '@zap-onboard/web-components';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { DeleteModal } from '../components';
import { useBillingPaymentSummary } from './hooks/useBillingAccountSubscriptions';
import { useBillingMutations } from './hooks/useBillingMutations';
import { InvoiceDisplay } from './InvoiceDisplay';

export const PaymentsPanel = ({
  billingAccount,
}: {
  billingAccount: StripeBillingAccountListingResponse;
}) => {
  const summary = useBillingPaymentSummary({
    billingAccountId: billingAccount.billingAccountId,
  });
  const deleteDisclosure = useDisclosure();

  return (
    <Stack spacing={6}>
      {summary.data?.upcomingInvoice && (
        <Box p={3} layerStyle="outline">
          <Text mb={6} fontWeight="bold" fontSize="sm">
            Upcoming invoice
          </Text>
          <InvoiceDisplay
            displaySummaryOutside
            invoice={summary.data?.upcomingInvoice}
          />
          <Button
            mt={6}
            variant="outline"
            colorScheme="red"
            onClick={deleteDisclosure.onOpen}
          >
            Cancel subscription
          </Button>
        </Box>
      )}
      <Box p={3} layerStyle="outline">
        <Text mb={6} fontWeight="bold" fontSize="sm">
          Previous invoices
        </Text>

        {summary.data?.invoices?.map((invoice) => (
          <InvoiceDisplay key={invoice.stripeInvoiceId} invoice={invoice} />
        ))}
      </Box>
      <CancelSubscriptionModal
        billingAccountId={billingAccount.billingAccountId}
        disclosure={deleteDisclosure}
      />
    </Stack>
  );
};

const CancelSubscriptionModal = (props: {
  billingAccountId: string;
  disclosure: ReturnType<typeof useDisclosure>;
}) => {
  const { billingAccountId, disclosure } = props;

  const { cancelSubscription, isCancelling } = useBillingMutations();

  const form = useForm({
    resolver: zodResolver(
      z.object({
        reason: z.string().min(3, 'Please provide a reason for cancellation'),
      }),
    ),
  });

  return (
    <Modal closeOnOverlayClick {...disclosure}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="deleteModalHeader">
          Cancel your Subscription?
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            <Text fontSize="sm">
              Are you sure you want to cancel your subscription? Any credits
              will be applied to the billing account at the end of the billing
              period.
            </Text>

            <Text fontSize="xs">
              If you have a moment, please let us know why you are cancelling
            </Text>

            <FormProvider {...form}>
              <Stack spacing={4}>
                <inputs.Textarea
                  name="reason"
                  label="Reason for cancellation"
                  isRequired
                />
              </Stack>
            </FormProvider>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            data-testid="deleteModalButton"
            mr={3}
            variant="danger"
            isLoading={isCancelling}
            onClick={form.handleSubmit(async ({ reason }) => {
              await cancelSubscription({
                billingAccountId,
                reason,
              });
              disclosure.onClose();
            })}
          >
            Cancel subscription
          </Button>
          <Button
            disabled={isCancelling}
            variant="outline"
            onClick={disclosure.onClose}
          >
            Keep subscription
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
